import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppTopBar, Breadcrumbs } from '../../components/AppTopBar.js';
import { ApiError } from '../../lib/api.js';
import {
  addChecklistItem,
  createChecklistTemplate,
  fetchChecklistItems,
  fetchChecklistTemplates,
  type ChecklistTemplate,
} from '../../lib/audit-api.js';
import { t } from '../../lib/i18n.js';

/**
 * Checklist templates (spec §11; FN-10). Authored once, as master data, and
 * then chosen on each audit. Used to need the QMS360 support team.
 *
 * Add-only: a template or question in use is part of past audits' records, so
 * it is not edited here (OPEN_QUESTIONS Q14 covers where checklists come from).
 */
export function AuditChecklists() {
  const queryClient = useQueryClient();
  const templates = useQuery({ queryKey: ['checklist-templates'], queryFn: fetchChecklistTemplates, retry: false });
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => createChecklistTemplate({ name: name.trim() }),
    onSuccess: (result) => {
      setName('');
      setError(null);
      setOpen(result.template.id);
      void queryClient.invalidateQueries({ queryKey: ['checklist-templates'] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The checklist could not be created.")),
  });

  return (
    <div className="min-h-full">
      <AppTopBar app="audit" />
      <Breadcrumbs app="audit" trail={['Checklist templates']} />
      <main className="mx-auto max-w-4xl px-4 pb-12 pt-4">
        <h1 className="m-0 text-2xl font-semibold">{t("Checklist templates")}</h1>
        <p className="mt-1 text-sm text-content-secondary">
          {t("Written once and chosen on each audit. Questions are added in order; one already answered on an audit stays as it was asked.")}
        </p>

        <form
          aria-label={t("New checklist")}
          className="mt-4 flex flex-wrap items-end gap-2"
          onSubmit={(event) => {
            event.preventDefault();
            create.mutate();
          }}
        >
          <label className="flex flex-1 flex-col text-sm">
            <span className="mb-1 text-content-secondary">{t("New checklist name")}</span>
            <input className="rounded border border-border-strong px-2 py-1.5" value={name} onChange={(event) => setName(event.target.value)} />
          </label>
          <button type="submit" className="rounded bg-ag-purple px-3 py-1.5 text-sm text-white disabled:opacity-60" disabled={!name.trim() || create.isPending}>
            {t("Create")}
          </button>
        </form>
        {error && (
          <p role="alert" className="mt-2 text-sm" style={{ color: 'var(--tone-critical)' }}>
            {error}
          </p>
        )}

        {templates.error && (
          <p role="status" className="mt-6 text-sm text-content-secondary">
            {t("Writing checklists needs configuration authority in QMS Audit.")}
          </p>
        )}
        <ul className="mt-6 list-none space-y-3 p-0">
          {(templates.data?.templates ?? []).map((template) => (
            <li key={template.id} className="rounded-md border border-border bg-surface p-4">
              <button
                type="button"
                className="text-start text-base font-semibold text-ag-purple"
                aria-expanded={open === template.id}
                onClick={() => setOpen(open === template.id ? null : template.id)}
              >
                {template.name}
              </button>
              {open === template.id && <TemplateItems template={template} />}
            </li>
          ))}
        </ul>
      </main>
    </div>
  );
}

function TemplateItems({ template }: { template: ChecklistTemplate }) {
  const queryClient = useQueryClient();
  const items = useQuery({ queryKey: ['checklist-items', template.id], queryFn: () => fetchChecklistItems(template.id) });
  const [clause, setClause] = useState('');
  const [question, setQuestion] = useState('');
  const [guidance, setGuidance] = useState('');
  const [error, setError] = useState<string | null>(null);
  const list = items.data?.items ?? [];

  const add = useMutation({
    mutationFn: () =>
      addChecklistItem(template.id, {
        clause: clause.trim() || null,
        question: question.trim(),
        guidance: guidance.trim() || null,
        sequence: list.length,
      }),
    onSuccess: () => {
      setClause('');
      setQuestion('');
      setGuidance('');
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['checklist-items', template.id] });
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The question could not be added.")),
  });

  return (
    <div className="mt-3 text-sm">
      {list.length === 0 ? (
        <p className="m-0 text-content-secondary">{t("No questions yet.")}</p>
      ) : (
        <ol className="m-0 space-y-1 ps-5">
          {list.map((item) => (
            <li key={item.id}>
              {item.clause ? <span className="text-content-secondary">{item.clause} · </span> : null}
              {item.question}
              {item.guidance ? <span className="block text-xs text-content-secondary">{item.guidance}</span> : null}
            </li>
          ))}
        </ol>
      )}
      <form
        aria-label={t("Add a question to {0}", { 0: template.name })}
        className="mt-3 grid gap-2 sm:grid-cols-4"
        onSubmit={(event) => {
          event.preventDefault();
          add.mutate();
        }}
      >
        <label className="flex flex-col">
          <span className="mb-1 text-content-secondary">{t("Clause")}</span>
          <input className="rounded border border-border-strong px-2 py-1.5" value={clause} onChange={(event) => setClause(event.target.value)} />
        </label>
        <label className="flex flex-col sm:col-span-3">
          <span className="mb-1 text-content-secondary">{t("Question")}</span>
          <input className="rounded border border-border-strong px-2 py-1.5" value={question} onChange={(event) => setQuestion(event.target.value)} />
        </label>
        <label className="flex flex-col sm:col-span-4">
          <span className="mb-1 text-content-secondary">{t("Guidance for the auditor (optional)")}</span>
          <input className="rounded border border-border-strong px-2 py-1.5" value={guidance} onChange={(event) => setGuidance(event.target.value)} />
        </label>
        <button type="submit" className="w-fit rounded bg-ag-purple px-3 py-1.5 text-white disabled:opacity-60" disabled={!question.trim() || add.isPending}>
          {t("Add the question")}
        </button>
      </form>
      {error && (
        <p role="alert" className="mt-2" style={{ color: 'var(--tone-critical)' }}>
          {error}
        </p>
      )}
    </div>
  );
}
